/**
 * oracleApi.ts - Oracle Insights API service
 *
 * Handles fetching Oracle insights from the backend with mock data fallback.
 * Per reasoning-oracle-prototype-selection.md Section 7 and 10.2
 */

import type { OracleResponse } from '@models/oracle'
import { isRefusedResponse, REFUSAL_FALLBACK_MESSAGE } from '@utils/refusal'

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000'
const USE_MOCK_DATA = import.meta.env.VITE_ORACLE_MOCK === 'true' // Default to live backend (Phase 6 complete)

// Expected API version for contract validation
const EXPECTED_API_VERSION = '1.0'

/**
 * Error response format per Section 7.3
 */
export interface OracleErrorResponse {
  error: string
  message: string
  details?: {
    component?: string
    query?: string
    conversation_id?: string
    fallback_available?: boolean
  }
}

/**
 * Custom error class for Oracle API errors
 */
export class OracleApiError extends Error {
  public readonly code: string
  public readonly statusCode: number
  public readonly details?: OracleErrorResponse['details']
  public readonly fallbackAvailable: boolean

  constructor(response: OracleErrorResponse, statusCode: number) {
    super(response.message)
    this.name = 'OracleApiError'
    this.code = response.error
    this.statusCode = statusCode
    this.details = response.details
    this.fallbackAvailable = response.details?.fallback_available ?? false
  }
}

/**
 * Parse error response from API
 */
async function parseErrorResponse(response: Response): Promise<OracleApiError> {
  try {
    const errorData = (await response.json()) as OracleErrorResponse
    return new OracleApiError(errorData, response.status)
  } catch {
    // If JSON parsing fails, create generic error
    return new OracleApiError(
      {
        error: response.status === 404 ? 'not_found' : 'unknown_error',
        message: `Oracle API error: ${response.status} ${response.statusText}`,
      },
      response.status
    )
  }
}

/**
 * Normalized response from Oracle API (supports history)
 */
export interface OracleInsightsResult {
  responses: OracleResponse[]
  latest: OracleResponse | null
  /** True when the backend scope gate blocked the prompt (Plan 2D-a). */
  refused?: boolean
  /** Human-readable refusal text; only populated when `refused === true`. */
  message?: string
}

/**
 * Fetch Oracle insights for a conversation
 * Returns normalized result with full history array
 */
export async function fetchOracleInsights(conversationId: string): Promise<OracleInsightsResult> {
  if (USE_MOCK_DATA) {
    console.log('[OracleAPI] Using mock data for conversation:', conversationId)
    const mock = getMockOracleResponse(conversationId)
    return { responses: [mock], latest: mock }
  }

  try {
    const token = localStorage.getItem('jwt_token')
    const response = await fetch(
      `${API_BASE_URL}/oracle/insights?conversation_id=${conversationId}`,
      { headers: token ? { 'Authorization': `Bearer ${token}` } : {} }
    )

    if (!response.ok) {
      const error = await parseErrorResponse(response)
      console.error('[OracleAPI] Error response:', {
        code: error.code,
        message: error.message,
        statusCode: error.statusCode,
        fallbackAvailable: error.fallbackAvailable,
      })
      throw error
    }

    const data = await response.json()
    return normalizeOracleResponse(data)
  } catch (error) {
    if (error instanceof OracleApiError) {
      throw error
    }
    console.error('[OracleAPI] Error fetching insights:', error)
    throw error
  }
}

/**
 * Normalize API response — handles old single-response format and new array format
 */
function normalizeOracleResponse(data: any): OracleInsightsResult {
  // Refusal short-circuit: scope classifier blocked the prompt (Plan 2D-a).
  // Runs BEFORE validation because refusal payloads intentionally lack the
  // fields validateOracleResponse requires.
  if (isRefusedResponse(data)) {
    return {
      responses: [],
      latest: null,
      refused: true,
      message: data.response || REFUSAL_FALLBACK_MESSAGE,
    }
  }

  // New format: { responses: [...], latest: {...}, count: N }
  if (data.responses && Array.isArray(data.responses)) {
    const validated = data.responses.map((r: any) => validateOracleResponse(r))
    return {
      responses: validated,
      latest: validated.length > 0 ? validated[0] : null,
    }
  }

  // Old format: single OracleResponse with _api_version
  if (data._api_version || data.query) {
    const validated = validateOracleResponse(data)
    return { responses: [validated], latest: validated }
  }

  throw new Error('Unrecognized Oracle API response format')
}

/**
 * Validate that the response matches the API contract
 */
function validateOracleResponse(data: unknown): OracleResponse {
  const response = data as OracleResponse

  // Check API version
  if (response._api_version && response._api_version !== EXPECTED_API_VERSION) {
    console.warn(
      `[OracleAPI] API version mismatch: expected ${EXPECTED_API_VERSION}, got ${response._api_version}`
    )
  }

  // Validate required fields
  const requiredFields = [
    'query',
    'classification',
    'inference_chains',
    'confidence',
    'explanation',
  ]

  for (const field of requiredFields) {
    if (!(field in response)) {
      throw new Error(`Invalid Oracle response: missing field '${field}'`)
    }
  }

  return response
}

/**
 * Get mock Oracle response for development
 */
function getMockOracleResponse(conversationId: string): OracleResponse {
  // Return different mock data based on conversation for variety
  const mockIndex = parseInt(conversationId.replace(/\D/g, '') || '0', 10) % 3

  const mockResponses: OracleResponse[] = [
    // Mock 1: Training → Retention query
    {
      _api_version: '1.0',
      query: 'How does employee training affect customer retention?',
      classification: {
        primary_reasoning: 'CAUSAL',
        secondary_reasoning: ['DIAGNOSTIC'],
        direction: 'downstream',
        anchor_concepts: [
          {
            concept: 'employee_training',
            node_id: 'human_capital_id',
            perspective: 'Learning_Growth',
            confidence: 0.95,
          },
          {
            concept: 'customer_retention',
            node_id: 'customer_retention_id',
            perspective: 'Customer',
            confidence: 0.92,
          },
        ],
        bsc_perspectives: ['Learning_Growth', 'Internal_Process', 'Customer'],
        complexity: 'MEDIUM',
      },
      inference_chains: [
        {
          anchor_concept: 'employee_training',
          direction: 'downstream',
          schema_id: 'bsc_vertical_causation_001',
          matched_nodes: [
            'human_capital_id',
            'service_quality_id',
            'customer_satisfaction_id',
            'customer_retention_id',
          ],
          gaps: [],
        },
        {
          anchor_concept: 'customer_retention',
          direction: 'upstream',
          schema_id: 'customer_economics_001',
          matched_nodes: ['service_quality_id', 'product_quality_id'],
          gaps: ['customer_onboarding'],
        },
      ],
      confidence: {
        score: 0.89,
        level: 'HIGH',
        factors: {
          schema_confidence: 0.9,
          graph_validation: 0.88,
          chain_completeness: 0.92,
          llm_certainty: 0.85,
        },
        method_modifier: 0.95,
        adjustments: ['Transformation schema applied'],
        explanation:
          'High confidence inference supported by established BSC causation patterns.',
      },
      explanation: {
        narrative: `Analysis of: "How does employee training affect customer retention?"

This causal query was analyzed by tracing effects and outcomes. The analysis spans BSC perspectives: Learning_Growth -> Internal_Process -> Customer.

Inference Chains:

1. From 'employee_training' (downstream):
   Using BSC Vertical Causation (cross-perspective causal chains)
   Path: Human Capital -> Service Quality -> Customer Satisfaction -> Customer Retention
   Strength: Strong inference (schema-backed, fully validated)

2. From 'customer_retention' (upstream):
   Using Customer Economics Model (retention-to-revenue causation)
   Path: Service Quality -> Product Quality
   Strength: Moderate inference (schema-backed, partially validated)

Gaps Identified: The following concepts were not found in the ontology:
  - customer_onboarding

Overall Confidence: HIGH (89%)
High confidence inference supported by established patterns.`,
        structured: `Query: How does employee training affect customer retention?
Type: causal | Direction: downstream
Perspectives: Learning_Growth, Internal_Process, Customer

Chain 1: employee_training (downstream)
  Schema: bsc_vertical_causation_001
  Strength: strong
  Steps:
    1. Human Capital (human_capital_id)
    2. Service Quality (service_quality_id)
    3. Customer Satisfaction (customer_satisfaction_id)
    4. Customer Retention (customer_retention_id)

Chain 2: customer_retention (upstream)
  Schema: customer_economics_001
  Strength: moderate
  Steps:
    1. Service Quality (service_quality_id)
    2. Product Quality (product_quality_id)
  Gaps: customer_onboarding

Confidence: HIGH (89%)`,
        summary:
          'Causal analysis of effects of employee_training: 2 inference chains, HIGH confidence (89%)',
        chain_explanations: [
          {
            chain_index: 0,
            anchor: 'employee_training',
            direction: 'downstream',
            strength: 'STRONG',
            narrative:
              "Looking downstream from 'employee_training' to find effects/outcomes",
            key_insight: 'BSC Vertical Causation (cross-perspective causal chains)',
          },
          {
            chain_index: 1,
            anchor: 'customer_retention',
            direction: 'upstream',
            strength: 'MODERATE',
            narrative:
              "Looking upstream from 'customer_retention' to find causes/enablers",
            key_insight: 'Customer Economics Model (retention-to-revenue causation)',
          },
        ],
        overall_strength: 'STRONG',
        confidence_score: 0.89,
        bsc_flow: ['Learning_Growth', 'Internal_Process', 'Customer'],
        gaps_identified: ['customer_onboarding'],
        limitations: [
          '1 concept(s) not found in ontology - consider ontology expansion',
        ],
      },
      schemas_activated: ['bsc_vertical_causation_001', 'customer_economics_001'],
      fallback_used: false,
      fallback_reason: '',
      additional_anchors: [
        'human_capital_id',
        'service_quality_id',
        'customer_satisfaction_id',
      ],
      suggested_paths: [
        [
          'employee_training',
          'human_capital_id',
          'service_quality_id',
          'customer_retention_id',
        ],
      ],
    },

    // Mock 2: Revenue impact query
    {
      _api_version: '1.0',
      query: "What's causing our revenue decline?",
      classification: {
        primary_reasoning: 'DIAGNOSTIC',
        secondary_reasoning: ['CAUSAL'],
        direction: 'upstream',
        anchor_concepts: [
          {
            concept: 'revenue',
            node_id: 'revenue_id',
            perspective: 'Financial',
            confidence: 0.98,
          },
        ],
        bsc_perspectives: ['Customer', 'Internal_Process', 'Financial'],
        complexity: 'HIGH',
      },
      inference_chains: [
        {
          anchor_concept: 'revenue',
          direction: 'upstream',
          schema_id: 'customer_economics_001',
          matched_nodes: [
            'customer_retention_id',
            'customer_satisfaction_id',
            'service_quality_id',
          ],
          gaps: ['market_conditions'],
        },
      ],
      confidence: {
        score: 0.78,
        level: 'MEDIUM',
        factors: {
          schema_confidence: 0.85,
          graph_validation: 0.75,
          chain_completeness: 0.8,
          llm_certainty: 0.72,
        },
        method_modifier: 0.95,
        adjustments: ['Diagnostic query adjustment'],
        explanation:
          'Medium confidence - some external factors not represented in ontology.',
      },
      explanation: {
        narrative: `Analysis of: "What's causing our revenue decline?"

This diagnostic query was analyzed by identifying root causes and enablers.

Revenue decline may be traced to:
- Customer retention issues
- Customer satisfaction problems
- Service quality deficiencies

External factor 'market_conditions' was hypothesized but not found in ontology.`,
        structured: `Query: What's causing our revenue decline?
Type: diagnostic | Direction: upstream
Perspectives: Customer, Internal_Process, Financial

Chain 1: revenue (upstream)
  Schema: customer_economics_001
  Strength: moderate
  Steps:
    1. Customer Retention (customer_retention_id)
    2. Customer Satisfaction (customer_satisfaction_id)
    3. Service Quality (service_quality_id)
  Gaps: market_conditions

Confidence: MEDIUM (78%)`,
        summary:
          'Diagnostic analysis of causes of revenue: 1 inference chain, MEDIUM confidence (78%)',
        chain_explanations: [
          {
            chain_index: 0,
            anchor: 'revenue',
            direction: 'upstream',
            strength: 'MODERATE',
            narrative: "Looking upstream from 'revenue' to find causes/enablers",
            key_insight: 'Customer Economics Model (retention-to-revenue causation)',
          },
        ],
        overall_strength: 'MODERATE',
        confidence_score: 0.78,
        bsc_flow: ['Internal_Process', 'Customer', 'Financial'],
        gaps_identified: ['market_conditions'],
        limitations: [
          '1 concept(s) not found in ontology - consider ontology expansion',
          'Confidence below 85% - treat as hypothesis',
        ],
      },
      schemas_activated: ['customer_economics_001'],
      fallback_used: false,
      fallback_reason: '',
      additional_anchors: ['customer_retention_id', 'service_quality_id'],
      suggested_paths: [
        ['revenue', 'customer_retention_id', 'customer_satisfaction_id'],
      ],
    },

    // Mock 3: Process improvement query
    {
      _api_version: '1.0',
      query: 'How can we improve operational efficiency?',
      classification: {
        primary_reasoning: 'CAUSAL',
        secondary_reasoning: [],
        direction: 'both',
        anchor_concepts: [
          {
            concept: 'operational_efficiency',
            node_id: 'operational_efficiency_id',
            perspective: 'Internal_Process',
            confidence: 0.91,
          },
        ],
        bsc_perspectives: ['Learning_Growth', 'Internal_Process', 'Financial'],
        complexity: 'MEDIUM',
      },
      inference_chains: [
        {
          anchor_concept: 'operational_efficiency',
          direction: 'upstream',
          schema_id: 'bsc_vertical_causation_001',
          matched_nodes: ['human_capital_id', 'information_capital_id', 'training_id'],
          gaps: [],
        },
        {
          anchor_concept: 'operational_efficiency',
          direction: 'downstream',
          schema_id: 'value_chain_flow_001',
          matched_nodes: ['cost_reduction_id', 'profit_margin_id'],
          gaps: [],
        },
      ],
      confidence: {
        score: 0.92,
        level: 'HIGH',
        factors: {
          schema_confidence: 0.95,
          graph_validation: 0.9,
          chain_completeness: 1.0,
          llm_certainty: 0.88,
        },
        method_modifier: 0.95,
        adjustments: [],
        explanation: 'High confidence with complete chain validation.',
      },
      explanation: {
        narrative: `Analysis of: "How can we improve operational efficiency?"

This causal query explores both causes and effects of operational efficiency.

Enablers (upstream):
- Human Capital investments
- Information Capital (systems, technology)
- Training programs

Effects (downstream):
- Cost Reduction
- Improved Profit Margin

All hypothesized concepts validated in ontology.`,
        structured: `Query: How can we improve operational efficiency?
Type: causal | Direction: both
Perspectives: Learning_Growth, Internal_Process, Financial

Chain 1: operational_efficiency (upstream)
  Schema: bsc_vertical_causation_001
  Strength: strong

Chain 2: operational_efficiency (downstream)
  Schema: value_chain_flow_001
  Strength: strong

Confidence: HIGH (92%)`,
        summary:
          'Causal analysis of relationships around operational_efficiency: 2 inference chains, HIGH confidence (92%)',
        chain_explanations: [
          {
            chain_index: 0,
            anchor: 'operational_efficiency',
            direction: 'upstream',
            strength: 'STRONG',
            narrative:
              "Looking upstream from 'operational_efficiency' to find enablers",
            key_insight: 'BSC Vertical Causation (cross-perspective causal chains)',
          },
          {
            chain_index: 1,
            anchor: 'operational_efficiency',
            direction: 'downstream',
            strength: 'STRONG',
            narrative:
              "Looking downstream from 'operational_efficiency' to find effects",
            key_insight: 'Value Chain Flow (activity-to-outcome sequences)',
          },
        ],
        overall_strength: 'STRONG',
        confidence_score: 0.92,
        bsc_flow: ['Learning_Growth', 'Internal_Process', 'Financial'],
        gaps_identified: [],
        limitations: [],
      },
      schemas_activated: ['bsc_vertical_causation_001', 'value_chain_flow_001'],
      fallback_used: false,
      fallback_reason: '',
      additional_anchors: ['human_capital_id', 'cost_reduction_id'],
      suggested_paths: [
        ['human_capital_id', 'operational_efficiency_id', 'cost_reduction_id'],
      ],
    },
  ]

  return mockResponses[mockIndex]
}

/**
 * Trigger Oracle analysis for a query
 * Returns normalized result with full history array
 */
export async function triggerOracleAnalysis(
  query: string,
  conversationId: string
): Promise<OracleInsightsResult> {
  if (USE_MOCK_DATA) {
    console.log('[OracleAPI] Using mock data for query:', query)
    // Simulate processing delay
    await new Promise((resolve) => setTimeout(resolve, 1500))
    const mock = getMockOracleResponse(conversationId)
    return { responses: [mock], latest: mock }
  }

  try {
    const token = localStorage.getItem('jwt_token')
    const response = await fetch(`${API_BASE_URL}/oracle/analyze`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        query,
        conversation_id: conversationId,
      }),
    })

    if (!response.ok) {
      const error = await parseErrorResponse(response)
      console.error('[OracleAPI] Error response:', {
        code: error.code,
        message: error.message,
        statusCode: error.statusCode,
        fallbackAvailable: error.fallbackAvailable,
      })
      throw error
    }

    const data = await response.json()
    return normalizeOracleResponse(data)
  } catch (error) {
    if (error instanceof OracleApiError) {
      throw error
    }
    console.error('[OracleAPI] Error triggering analysis:', error)
    throw error
  }
}
